import { useCallback, useMemo, useEffect } from 'react'
import { Formik, Form } from 'formik'
import responseFromSearchParam from 'FlowBuilder/lib/search-params-response-parser'
import { getValidationSchema, transformQuestionsWithTargetDetails } from 'FlowTaker/lib/formHelpers'
import Question from './Question'
import useFlowTranslate from 'FlowBuilder/hooks/useFlowTranslate'
import useQuestionTranslate from 'FlowBuilder/hooks/useQuestionTranslate'
import useActionTranslate from 'FlowBuilder/hooks/useActionTranslate'
import liquidEngine from 'FlowBuilder/components/ActionViews/liquidEngine'

export default function TakeSurveyForm({ surveyId, actionId, pageNumber, onSubmit, onUpdateNoTarget = () => { } }) {
  const questions = useSel(s => {
    const allQuestions = _.values(s.questions.entities).filter(q => q.surveyId === surveyId && q.active)
    const pageQuestions = allQuestions.filter(q => q.pageNumber === pageNumber)
    return _.orderBy(pageQuestions, 'digitOrder')
  })

  const [hasSelectedNo, setHasSelectedNo] = useState(false)
  const optOutQuestion = useMemo(() =>
    questions.find(q => q.questionType === 'opt_in_radio' && q?.settings?.optOutPrompt),
    [questions]
  )
  const handleOptionChange = useCallback((values) => {
    if (optOutQuestion && values[optOutQuestion.id] === 'n') {
      setHasSelectedNo(true)
    } else {
      setHasSelectedNo(false)
    }
  }, [optOutQuestion])

  const targetDetails = useSel(s => s.taker?.targetDetails || {})
  const transformedQuestions = transformQuestionsWithTargetDetails(questions, targetDetails)

  const pageCount = useSel(s => {
    const allQuestions = _.values(s.questions.entities).filter(q => q.surveyId === surveyId && q.active)
    return _.max(allQuestions.map(q => q.pageNumber))
  })

  const validationSchema = getValidationSchema(transformedQuestions)

  const initialValues = questions.reduce((acc, q) => {
    acc[q.id] = acc[q.id] || responseFromSearchParam(q)
    return acc
  }, {})

  const { fallback: { buttonTextSubmit, buttonTextNext } } = useFlowTranslate()
  const { fallback: { optOutTitle: optOutQuestionTitle, optOutMessage: optOutQuestionText } } = useQuestionTranslate({ questionOverride: optOutQuestion })
  const { fallback: { signingAsText: signingAsTextRaw, notMeText } } = useActionTranslate({ actionId })

  let buttonText = buttonTextNext
  if (pageNumber === pageCount) {
    buttonText = buttonTextSubmit
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={(values) => { onSubmit(values) }}
    >
      {({ isSubmitting, values }) => {
        useEffect(() => {
          handleOptionChange(values)
        }, [values, handleOptionChange])
        return (
          <Form className="form">
            {transformedQuestions.map((q) => {
              if (q.questionType === 'detailsMessage') {
                return <DetailsMessage key="detailsMessage" onUpdateNoTarget={onUpdateNoTarget} signedInAs={q.signedInAs} signingAsTextRaw={signingAsTextRaw} notMeText={notMeText} />
              } else if (q.hide) {
                return null
              } else {
                return <>
                  <Question key={q.id} question={q} />
                  {hasSelectedNo && q.questionType === 'opt_in_radio' && q.id === optOutQuestion.id && (
                    <div className="field">
                      <label>{optOutQuestionTitle ?? 'Are you sure?' }</label>
                      <p>{optOutQuestionText ?? optOutQuestion?.settings.optOutMessage}</p>
                    </div>
                  )}
                </>
              }
            })}
            <button
              type="submit"
              className="submit-button button primary"
              disabled={isSubmitting}
            >
              {buttonText}
            </button>
          </Form >
        )
      }}
    </Formik>
  )
}

function DetailsMessage({ onUpdateNoTarget, signedInAs, signingAsTextRaw, notMeText }) {
  const signingAsText = useMemo(() => liquidEngine.parseAndRenderSync(signingAsTextRaw, { name: signedInAs }), [signingAsTextRaw, signedInAs])

  function onClick() {
    onUpdateNoTarget()
  }

  return (
    <div className="field target-details-message">
      <label>{signingAsText}</label>
      <div className='not-me-link' onClick={onClick}>{notMeText}</div>
    </div>
  )
}
